import { EventEmitter } from "eventemitter3";
import { randomToken, FileStatus } from "./utils";

export let tasks = [];
export let fileItems = [];

/**
 * tasks
 * @typedef {Object} Task {tid, pid, name, isdir, size, status, behavior }
 * @typedef {Object} FileItem { fid, tid, status, md5, uploadId, blockSize, blockNum, file, metadatas}
 */
export class TaskService extends EventEmitter {
  constructor(options) {
    super();
    this._logger = options.logger;
  }

  clean() {
    this._logger.log("[taskService] 任务被清空 clean");
    tasks = [];
    fileItems = [];
  }

  /**
   * add new folder to upload queue
   */
  addFolder(files, pid, behavior = "RENAME") {
    if (files == null) return false;
    if (files.length === 0) return false;
    const { webkitRelativePath } = files[0];
    const taskName = webkitRelativePath.split("/")[0];
    let totalSize = 0;
    const tid = randomToken();
    for (const file of files) {
      const fid = randomToken();
      const item = {
        code: 0,
        tid: tid,
        fid: fid,
        md5: null,
        file: file,
        metadatas: [],
        uploadId: null,
        status: FileStatus.WAITING,
      };
      fileItems.push(item);
      totalSize += file.size;
    }
    const task = {
      tid: tid,
      pid: pid,
      name: taskName,
      isdir: true,
      size: totalSize,
      behavior: behavior,
      status: FileStatus.WAITING,
    };
    tasks.push(task);
    this.emit("NewTasks", [task]);
    return true;
  }

  // add new files to upload queue
  addFiles(files, pid, behavior = "RENAME") {
    if (files == null) return false;
    if (files.length === 0) return false;
    const items = [];
    for (const file of files) {
      const tid = randomToken();
      const task = {
        tid: tid,
        pid: pid,
        isdir: false,
        fileId: null,
        name: file.name,
        size: file.size,
        behavior: behavior,
        status: FileStatus.WAITING,
        message: "",
      };
      items.push(task);
      tasks.push(task);
      const fid = randomToken();
      const item = {
        code: 0,
        tid: tid,
        fid: fid,
        md5: null,
        uploadId: null,
        file: file,
        metadatas: [],
        status: FileStatus.WAITING,
      };
      fileItems.push(item);
    }
    this.emit("NewTasks", items);
    return true;
  }

  /**
   * remove task by task id
   */
  remove(tid) {
    const aborts = [];
    const items = fileItems.filter((t) => t.tid === tid);
    for (const item of items) {
      if (item.status === FileStatus.COMPLETE) continue; 
      if (item.fileId && item.uploadId) {
        aborts.push({ fileId: item.fileId, uploadId: item.uploadId });
      }
    }
    fileItems = fileItems.filter((t) => t.tid !== tid);
    tasks = tasks.filter((t) => t.tid !== tid);
    this.emit("RemoveTask", tid);
    return aborts;
  }

  /**
   * get next file to upload
   * 1. get a new task
   * 2. change status to uploading
   * @returns { task, fileItem, uploadedBytes, totalBytes}
   */
  next() {
    const waiting = FileStatus.WAITING;
    const fileItem = fileItems.find((t) => t.status === waiting);
    if (!fileItem) {
      this._logger.log("[taskService] no file to upload");
      this._logger.log("[taskService] fileItems: ", fileItems);
      return; // no file to upload
    }
    const task = tasks.find((t) => t.tid === fileItem.tid);
    if (!task) {
      this._logger.log("[taskService] no task");
      this._logger.log("[taskService] task: ", tasks);
      return; // no task
    }
    // get uploaded bytes
    let uploadedBytes = 0;
    if (task.status === FileStatus.WAITING) {
      uploadedBytes = this._getUplodedBytes(task.tid);
      task.status = FileStatus.UPLOADING;
      this.emit("TaskStatusChange", task.tid, FileStatus.UPLOADING);
    }
    // set status to uploading
    fileItem.status = FileStatus.UPLOADING;
    this.emit("FileStatusChange", fileItem.fid, task.tid, FileStatus.UPLOADING);
    return { task, fileItem, uploadedBytes };
  }

  _getUplodedBytes(tid) {
    const items = fileItems.filter(
      (t) => t.tid === tid && t.status === FileStatus.COMPLETE
    );
    let bytes = 0;
    for (const item of items) {
      bytes += item.file.size;
    }
    return bytes;
  }

  /**
   * update uploadId and task status when init upload completed
   */
  beforeUpload(tid, fid, data) {
    const { metadatas, md5, uploadId, blockInfo } = data;
    let blockNum,
      blockSize = 0;
    if (blockInfo) {
      blockNum = blockInfo.blockNum;
      blockSize = blockInfo.blockSize;
    }
    const task = tasks.find((t) => t.tid === tid);
    if (!task) return false;
    if (task.fileId == null) {
      task.fileId = metadatas[0].fileId;
    }
    const item = fileItems.find((t) => t.fid === fid);
    if (!item) return false;
    const fileId = metadatas[metadatas.length - 1].fileId;
    item.md5 = md5;
    item.fileId = fileId;
    item.uploadId = uploadId;
    item.blockNum = blockNum;
    item.blockSize = blockSize;
    item.metadatas = metadatas;
    return true;
  }

  /**
   * complete upload
   * @param {String} tid Task ID
   * @param {String} fid File ID
   * @returns
   */
  afterUpload(tid, fid, fn) {
    const task = tasks.find((t) => t.tid === tid);
    if (!task) {
      fn(true);
      return false;
    }
    const item = fileItems.find((t) => t.fid === fid);
    if (!item) {
      fn(true);
      return false;
    }
    item.status = FileStatus.COMPLETE;
    this.emit("FileStatusChange", fid, tid, FileStatus.COMPLETE);
    const index = fileItems.findIndex(
      (t) => t.status !== FileStatus.COMPLETE && t.tid === tid
    );
    if (index === -1) {
      task.status = FileStatus.COMPLETE;
      this.emit("TaskStatusChange", tid, FileStatus.COMPLETE);
      fn(true);
    } else {
      fn(false);
    }
    return true;
  }

  /**
   * pause upload from user
   * @param {String} id
   */
  pause(tid) {
    const pause = FileStatus.PAUSE;
    const waiting = FileStatus.WAITING;
    const uploading = FileStatus.UPLOADING;
    const items = fileItems.filter(
      (t) => t.tid === tid && (t.status === waiting || t.status === uploading)
    );
    if (items.length === 0) {
      this._logger.log(`[taskService] no file to pause: ${tid}`);
      this._logger.log(`[taskService] fileItems:`, items);
      return false;
    }
    for (const item of items) {
      item.status = pause;
      this.emit("FileStatusChange", item.fid, tid, pause);
    }
    const task = tasks.find(
      (t) => t.tid === tid && (t.status === waiting || t.status === uploading)
    );
    if (!task) {
      this._logger.log(`[taskService] no task to pause: ${tid}`);
      this._logger.log(`[taskService] tasks:`, tasks);
      return false;
    }
    task.status = pause;
    this.emit("TaskStatusChange", tid, pause);
    return true;
  }

  resume(tid) {
    const error = FileStatus.ERROR;
    const pause = FileStatus.PAUSE;
    const waiting = FileStatus.WAITING;
    const items = fileItems.filter(
      (t) => t.tid === tid && (t.status === pause || t.status === error)
    );
    if (items.length === 0) {
      this._logger.log(`[taskService] no file to resume: ${tid}`);
      this._logger.log(`[taskService] fileItems:`, items);
      return false;
    }
    for (const item of items) {
      item.code = 0;
      item.status = waiting;
      this.emit("FileStatusChange", item.fid, tid, waiting);
    }
    const task = tasks.find(
      (t) => t.tid === tid && (t.status === pause || t.status === error)
    );
    if (!task) {
      this._logger.log(`[taskService] no task to resume: ${tid}`);
      this._logger.log(`[taskService] tasks:`, tasks);
      return false;
    }
    task.status = waiting;
    this.emit("TaskStatusChange", tid, waiting);
    return true;
  }

  error(tid, fid, code, message) {
    const item = fileItems.find((t) => t.fid === fid && t.tid === tid);
    if (!item) return false;
    item.code = code;
    item.status = FileStatus.ERROR;
    this.emit("FileStatusChange", fid, tid, FileStatus.ERROR);
    const task = tasks.find((t) => t.tid === tid);
    if (!task) return false;
    task.status = FileStatus.ERROR;
    task.message = message;
    this.emit("TaskStatusChange", tid, FileStatus.ERROR);
  }
}
